import { Inject, Injectable, Optional } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DateTimeService } from '@y42/date-time';
import {
  ColumnTypeEnum,
  OperationModel,
  OperatorEnum,
  OperatorTypeEnum,
  OperatorValueTypeEnum,
} from '@y42/json-schema';
import { isNullish } from '@y42/util/nullish';
import { BehaviorSubject, combineLatest, map, Observable, Subject, switchMap } from 'rxjs';
import { DEFAULT_OPERATOR_TYPE } from './default-operator-type.token';
import {
  DATE_TIME_COLUMN_TYPES,
  getDefaultOperatorForColumnType,
  OperationInputForm,
  OperatorForm,
} from './operation-input.model';

@UntilDestroy()
@Injectable()
export class OperationInputFormService {
  constructor(
    private readonly dateTimeService: DateTimeService,
    @Optional() @Inject(DEFAULT_OPERATOR_TYPE) private readonly defaultOperatorType: OperatorTypeEnum | undefined,
  ) {
    this.updateValidity$$
      .pipe(
        map(() =>
          // Set `values` validators when `operator` value changes
          this.operationsFormArray.controls.map((form) =>
            form.controls.operator!.valueChanges.pipe(map(() => this.setValuesValidators(form))),
          ),
        ),
        switchMap((updates$) => combineLatest(updates$)),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private readonly columnType$$ = new BehaviorSubject<ColumnTypeEnum | null>(null);
  private readonly reset$$ = new Subject<void>();
  private readonly updateValidity$$ = new Subject<void>();

  readonly form = new FormGroup<OperationInputForm>({
    operations: new FormArray<FormGroup<OperatorForm>>([]),
  });
  readonly operationsFormArray = this.form.controls.operations;

  readonly reset$: Observable<void> = this.reset$$.pipe(untilDestroyed(this));

  setColumnType(columnType: ColumnTypeEnum) {
    this.columnType$$.next(columnType);
  }

  /**
   * Returns only valid `OperationModel` objects
   */
  getOperations(): OperationModel[] {
    return this.operationsFormArray.controls
      .filter((form) => form.valid)
      .map((form) => {
        let formVal = form.getRawValue() as OperationModel;
        const columnType = formVal.type;

        if (
          formVal.operator_type !== OperatorTypeEnum.Relative &&
          columnType &&
          DATE_TIME_COLUMN_TYPES.includes(columnType) &&
          formVal.values
        ) {
          formVal = {
            ...formVal,
            values: formVal.values.map((value) => this.castDateToISOFormat(value as Date, columnType)),
          };
        }
        return {
          ...formVal,
        };
      });
  }

  setOperations(operations: Array<Partial<OperationModel>>, columnType: ColumnTypeEnum) {
    this.operationsFormArray.clear({ emitEvent: false });

    if (!operations.length) {
      // Make sure that the form always contains at least one element
      operations = [{}];
    }

    operations.forEach((operation) => {
      const form = this.createOperationFormGroup(columnType, operation);
      this.operationsFormArray.push(form, { emitEvent: false });
    });
    this.operationsFormArray.updateValueAndValidity();
    this.updateValidity$$.next();
  }

  addOperation(columnType: ColumnTypeEnum, operator?: OperatorEnum) {
    const form = this.createOperationFormGroup(columnType, { operator });
    this.operationsFormArray.push(form);
    this.updateValidity$$.next();
  }

  removeOperation(index: number) {
    this.operationsFormArray.removeAt(index);
    this.updateValidity$$.next();
  }

  reset() {
    const columnType = this.operationsFormArray.controls[0]?.value.type;
    this.operationsFormArray.clear();
    this.form.reset({});
    if (columnType) {
      this.setOperations([], columnType);
    }
    this.updateValidity$$.next();
    this.reset$$.next();
  }

  private createOperationFormGroup(
    columnType: ColumnTypeEnum,
    operation?: Partial<OperationModel>,
  ): FormGroup<OperatorForm> {
    this.setColumnType(columnType);

    const form = new FormGroup<OperatorForm>({
      type: new FormControl(columnType, { validators: [Validators.required], nonNullable: true }),
      operator: new FormControl(getDefaultOperatorForColumnType(columnType), {
        validators: [Validators.required],
        nonNullable: true,
      }),
      operator_type: new FormControl(this.defaultOperatorType ?? OperatorTypeEnum.Absolute, { nonNullable: true }),
      value_type: new FormControl(OperatorValueTypeEnum.None, { nonNullable: true }),
      values: new FormControl<unknown[]>([], { nonNullable: true }),
    });

    if (this.defaultOperatorType === OperatorTypeEnum.Relative) {
      form.patchValue({ value_type: OperatorValueTypeEnum.Days });
    }

    if (operation) {
      const { operator_type, values } = operation;
      form.patchValue({ ...operation, values: [...(operation.values || [])] });

      if (operator_type !== OperatorTypeEnum.Relative && DATE_TIME_COLUMN_TYPES.includes(columnType) && values) {
        form.controls.values.patchValue(values.map((value) => this.castISOFormatToDate(value as string, columnType)));
      }
    }

    this.setValuesValidators(form);

    return form;
  }

  private setValuesValidators(form: FormGroup<OperatorForm>): void {
    const validators = this.getValuesValidators(form);
    form.controls.values.setValidators(validators);
    form.controls.values.updateValueAndValidity();

    const operator = form.controls.operator?.value;
    if (operator && [OperatorEnum.IsEmpty, OperatorEnum.IsNotEmpty].includes(operator)) {
      form.controls.values.setValue([]);
    }
  }

  private getValuesValidators(form: FormGroup<OperatorForm>): ValidatorFn[] {
    const operator = form.controls.operator?.value;
    if (!operator) {
      return [];
    }

    const someEmptyOrNullishValidator = (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as unknown[] | undefined;
      const valuesInvalid = value?.some((val) => val === '' || isNullish(val));
      return valuesInvalid ? { emptyOrNullishValues: true } : null;
    };

    switch (operator) {
      case OperatorEnum.IsNot:
      case OperatorEnum.Equals:
      case OperatorEnum.LessThan:
      case OperatorEnum.GreaterThan:
      case OperatorEnum.StartsWith:
      case OperatorEnum.EndsWith:
      case OperatorEnum.Contains:
      case OperatorEnum.NotContains:
        return [Validators.required, Validators.minLength(1), Validators.maxLength(1), someEmptyOrNullishValidator];

      case OperatorEnum.Between:
        return [Validators.required, Validators.minLength(2), Validators.maxLength(2), someEmptyOrNullishValidator];

      case OperatorEnum.In:
      case OperatorEnum.NotIn:
        return [Validators.required, Validators.minLength(1), someEmptyOrNullishValidator];

      case OperatorEnum.IsEmpty:
      case OperatorEnum.IsNotEmpty:
        return [Validators.maxLength(0)];
    }
  }

  private castISOFormatToDate(value: string, __: ColumnTypeEnum): Date {
    return this.dateTimeService.parseDate(value);
  }

  private castDateToISOFormat(value: Date, columnType: ColumnTypeEnum): string {
    if (columnType === ColumnTypeEnum.Date) {
      // we set start of the day of this date but in terms of changed timezone!
      const dateStartOfDay = this.dateTimeService.startOfDay(value);

      // we calculate time from start of today (now) till now
      const now = new Date();
      const nowStartOfDay = this.dateTimeService.startOfDay(now);
      const timeDiff = now.getTime() - nowStartOfDay.getTime();

      // we just add a time to start of our date object with timezone
      const dateWithTime = new Date(dateStartOfDay.getTime() + timeDiff);
      // dump as iso format
      return dateWithTime.toISOString();
    }

    return value.toISOString();
  }
}
